import setDynamicFormFieldValue from "@bevco/Store/Handlers/DynamicForm/SetFieldValue";
import { CustomState } from "@bevco/Store/Reducers";
import {
    DynamicFormOptionModel,
    getDynamicFormOptionDisplay,
    resolveDynamicFormOptions,
} from "@bevco/Utilities/dynamicFormOptions";
import { DynamicFormContext } from "@bevco/Widgets/DynamicForm/DynamicForm";
import DynamicFormFieldShell from "@bevco/Widgets/DynamicForm/DynamicFormFieldShell";
import { useRegisterDynamicFormFieldKeys } from "@bevco/Widgets/DynamicForm/useRegisterDynamicFormFieldKeys";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Select from "@insite/mobius/Select";
import React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    fieldKey = "fieldKey",
    label = "label",
    hintText = "hintText",
    options = "options",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.fieldKey]: string;
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.options]: DynamicFormOptionModel[];
        [fields.isRequired]: boolean;
    };
}

/** Maps Dynamic Form Redux state into widget props. */
const mapStateToProps = (state: CustomState, ownProps: OwnProps) => {
    const fieldKey = ownProps.fields.fieldKey || "";
    return {
        fieldKey,
        fieldValue: state.customDynamicForm?.fieldValues[fieldKey],
    };
};

const mapDispatchToProps = {
    setDynamicFormFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const widgetGroup = "SalesForce Dynamic" as any;

/** Salesforce Dynamic Form dropdown field widget. */
const DynamicDropdown: React.FC<Props> = ({
    id,
    fields: widgetFields,
    fieldKey,
    fieldValue,
    setDynamicFormFieldValue,
}) => {
    const { label, hintText, isRequired, options } = widgetFields;
    const formContext = React.useContext(DynamicFormContext);
    const [errorMessage, setErrorMessage] = React.useState("");
    const resolvedOptions = React.useMemo(() => resolveDynamicFormOptions(options), [options]);
    const hideWhenSingleOption = resolvedOptions.length <= 1;
    useRegisterDynamicFormFieldKeys(id, fieldKey ? [fieldKey] : []);

    // Match Contact Us Type of Request: one option → hide UI, still send that value.
    React.useEffect(() => {
        if (!fieldKey || resolvedOptions.length !== 1) {
            return;
        }
        const onlyValue = resolvedOptions[0].value;
        if (fieldValue !== onlyValue) {
            setDynamicFormFieldValue({ key: fieldKey, value: onlyValue });
        }
    }, [fieldKey, fieldValue, resolvedOptions, setDynamicFormFieldValue]);

    const validate = React.useCallback(() => {
        const needsValue = isRequired && resolvedOptions.length > 1;
        const error = needsValue && !fieldValue ? (siteMessage("Field_Required", label) as string) : "";
        setErrorMessage(error);
        return !error;
    }, [fieldValue, isRequired, label, resolvedOptions.length]);

    React.useEffect(() => {
        if (!fieldKey) {
            return undefined;
        }
        formContext.validators[fieldKey] = validate;
        return () => {
            formContext.validators[fieldKey] = undefined;
        };
    }, [fieldKey, validate, formContext.validators]);

    React.useEffect(() => {
        if (errorMessage && fieldValue) {
            setErrorMessage("");
        }
    }, [fieldValue, errorMessage]);

    if (!fieldKey || hideWhenSingleOption) {
        return null;
    }

    return (
        <DynamicFormFieldShell>
            <Select
                label={label}
                required={isRequired}
                value={fieldValue || ""}
                error={errorMessage}
                onChange={event => setDynamicFormFieldValue({ key: fieldKey, value: event.currentTarget.value })}
            >
                <option value="">{hintText || translate("Select")}</option>
                {resolvedOptions.map(option => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </Select>
        </DynamicFormFieldShell>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(DynamicDropdown),
    definition: {
        group: widgetGroup,
        displayName: "Dropdown",
        icon: "caret-down",
        fieldDefinitions: [
            {
                name: fields.fieldKey,
                displayName: "Salesforce Field (API Name)",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
            },
            {
                name: fields.options,
                displayName: "Options",
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => getDynamicFormOptionDisplay(item),
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "label",
                        displayName: "Label",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "value",
                        displayName: "Value (sent to Salesforce)",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                        tooltip: "API value submitted to Salesforce (can differ from the Label).",
                    },
                ],
                defaultValue: [],
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
